/**
 * Fog of war — RENDER-ONLY, for the local player's team. Kept entirely out of the
 * deterministic sim (each viewer sees differently; the sim runs full-information).
 *
 * Computes per-HEX visibility: a hex is "in vision" when it lies within the
 * line-of-sight radius of any own/ally unit OR building (vision is shared across
 * the team). The renderer then DARKENS terrain (and entities/resources) on
 * out-of-vision hexes and HIDES enemies standing in the dark — Age-of-Empires
 * style. (For now everything seen-once stays dim rather than going pure black;
 * true "unexplored = black, explored = dim" memory comes later.)
 */
import type { World } from "../sim/world";
import type { Eid } from "../sim/types";
import type { Ident } from "../sim/components";
import type { Registry } from "../data/registry";
import { SQRT3 } from "../sim/hex";

/** Last-seen snapshot of an enemy building the local team has discovered.
 *  Buildings don't move, so position is fixed; this is enough to test visibility
 *  and to draw a "remembered" blip on the minimap. The 3D renderer keeps the
 *  building's own mesh frozen for the last-seen visual (foundation/HP/etc.). */
export interface BuildingGhost {
  eid: Eid;
  x: number;
  z: number;
  owner: number;
  defId: string;
}

export class Fog {
  enabled = false;
  localTeam = -1;
  private rows = 0;
  private reg?: Registry;
  /** Per-hex flag (indexed by world.hkey = col*rows+row): 1 = currently in vision. */
  visibleHex = new Uint8Array(0);
  /** Cartography: enemy buildings ever seen, kept at their last-seen spot until
   *  we re-explore and observe they're gone. Keyed by eid (survives the entity's
   *  removal from the sim). */
  readonly buildingMemory = new Map<Eid, BuildingGhost>();

  /** `reg` lets vision read each entity's CURRENT data los (so a balance change
   *  applies immediately, even to a loaded game whose entities baked an older los
   *  at spawn). Falls back to the stored los component when absent. */
  setLocal(team: number, reg?: Registry): void {
    this.localTeam = team;
    this.reg = reg;
    this.enabled = true;
  }

  /** Vision radius (world units) of an own/ally entity, from current game data. */
  private visionRadius(world: World, eid: Eid, ident: Ident, tile: number): number {
    if (this.reg && ident.civ) {
      try {
        const losTiles = ident.kind === "building"
          ? this.reg.building(ident.civ, ident.defId).los
          : this.reg.unit(ident.civ, ident.defId).los;
        return losTiles * tile;
      } catch { /* unknown civ/def → fall back to the baked component */ }
    }
    const lc = world.los.get(eid);
    if (!lc) return 0;
    // unit los is stored in world units; building los in tiles.
    return world.building.has(eid) ? lc.los * tile : lc.los;
  }

  /** Recompute the local team's shared vision (rasterize each own/ally LOS circle
   *  into the hex grid). Cheap: O(sources * coveredHexes). */
  update(world: World): void {
    if (!this.enabled) return;
    const n = world.cols * world.rows;
    if (this.visibleHex.length !== n) this.visibleHex = new Uint8Array(n);
    this.rows = world.rows;
    this.visibleHex.fill(0);

    const size = world.size;
    const tile = SQRT3 * size;
    for (const [eid, ident] of world.ident) {
      if (ident.kind !== "unit" && ident.kind !== "building") continue; // only own units/buildings give vision
      // an unbuilt FOUNDATION grants no vision — placing a build site in the fog
      // must NOT reveal the map (the builders walking there reveal it locally).
      if (ident.kind === "building" && world.building.get(eid)?.isFoundation) continue;
      const o = world.owner.get(eid)?.player;
      if (o === undefined || world.players.get(o)?.team !== this.localTeam) continue;
      const tr = world.transform.get(eid);
      if (!tr) continue;
      const r = this.visionRadius(world, eid, ident, tile);
      if (r <= 0) continue;
      const r2 = r * r;
      const h0 = world.hexAtWorld(tr.pos.x, tr.pos.y);
      if (!h0) continue;
      const R = Math.ceil(r / size) + 2; // hex-cell reach (over-scan; distance-filtered)
      for (let dc = -R; dc <= R; dc++) {
        const c = h0.col + dc;
        if (c < 0 || c >= world.cols) continue;
        for (let dr = -R; dr <= R; dr++) {
          const rr = h0.row + dr;
          if (rr < 0 || rr >= world.rows) continue;
          const ctr = world.hexCenter(c, rr);
          const dx = ctr.x - tr.pos.x;
          const dz = ctr.y - tr.pos.y;
          if (dx * dx + dz * dz <= r2) this.visibleHex[c * this.rows + rr] = 1;
        }
      }
    }

    this.updateBuildingMemory(world);
  }

  /** Cartography bookkeeping (run after visibleHex is computed):
   *  - snapshot every enemy building currently in vision (its last-seen spot),
   *  - forget a remembered building only once we can SEE its spot and it's gone
   *    (re-explored to empty ground). A building that's merely fogged — or razed
   *    while fogged — stays remembered. */
  private updateBuildingMemory(world: World): void {
    for (const eid of world.building.keys()) {
      const o = world.owner.get(eid)?.player;
      if (o === undefined || world.players.get(o)?.team === this.localTeam) continue; // own/ally → always live, never remembered
      const tr = world.transform.get(eid);
      if (!tr) continue;
      const h = world.hexAtWorld(tr.pos.x, tr.pos.y);
      if (h && this.visibleHex[h.col * this.rows + h.row] === 1) {
        this.buildingMemory.set(eid, { eid, x: tr.pos.x, z: tr.pos.y, owner: o, defId: world.ident.get(eid)?.defId ?? "" });
      }
    }
    for (const [eid, g] of this.buildingMemory) {
      if (!world.building.has(eid) && this.isPosVisible(world, g.x, g.z)) this.buildingMemory.delete(eid);
    }
  }

  /** Is hex `hkey` (= col*rows+row, the terrain vertex-group index) in vision? */
  isHexVisible(hkey: number): boolean {
    return !this.enabled || this.visibleHex[hkey] === 1;
  }

  /** Is the hex under world point (x,z) currently in the local team's vision? */
  isPosVisible(world: World, x: number, z: number): boolean {
    if (!this.enabled) return true;
    const h = world.hexAtWorld(x, z);
    return !h || this.visibleHex[h.col * this.rows + h.row] === 1;
  }

  /** Owned by a player on a DIFFERENT team than the local one (an enemy). */
  isEnemyOwned(world: World, eid: Eid): boolean {
    if (!this.enabled) return false;
    const o = world.owner.get(eid)?.player;
    return o !== undefined && world.players.get(o)?.team !== this.localTeam;
  }

  /** Owned by the local team (own or ally). Everything else — enemies AND neutral
   *  wildlife — is hidden when out of vision (you shouldn't see animals roaming the
   *  fog any more than enemy scouts). */
  isLocalTeam(world: World, eid: Eid): boolean {
    if (!this.enabled) return true;
    const o = world.owner.get(eid)?.player;
    return o !== undefined && world.players.get(o)?.team === this.localTeam;
  }
}
